import type { AuthMeResponse, Permission, PermissionScope } from "@pulsar-anvil-studios/api-types";
import { AUTH_LOGOUT } from "@pulsar-anvil-studios/routes/auth";
import { type ReactNode, useRef, useState } from "react";
import { api, apiPath } from "@/api/client";
import { buildPermissionMap, checkPermission } from "@/lib/permissions";
import { AuthContext } from "./AuthContext";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [identity, setIdentity] = useState<AuthMeResponse | null>(null);
  const [isAuthLoading, setAuthLoading] = useState(false);

  // Ref mirrors identity synchronously so hasPermission works inside
  // TanStack Router beforeLoad guards (which run before React re-renders).
  const identityRef = useRef<AuthMeResponse | null>(null);
  const permissionMapRef = useRef(buildPermissionMap([]));

  const setIdentitySync = (value: AuthMeResponse | null) => {
    identityRef.current = value;
    permissionMapRef.current = buildPermissionMap(value?.permissions ?? []);
    setIdentity(value);
  };

  const hasPermission = (permission: Permission, context?: Partial<PermissionScope>) =>
    checkPermission(permissionMapRef.current, permission, context);

  const logout = async () => {
    try {
      await api.post(apiPath(AUTH_LOGOUT));
    } catch {
      // fire-and-forget
    }
    setIdentitySync(null);
    // Full reload intentional — clears all client-side state on session expiry
    window.location.href = "/login";
  };

  return (
    <AuthContext
      value={{
        identity,
        isAuthenticated: identity !== null,
        isAuthLoading,
        setIdentity: setIdentitySync,
        setAuthLoading,
        hasPermission,
        logout,
      }}
    >
      {children}
    </AuthContext>
  );
};
